import {
  Box,
  Paper,
  Avatar,
  Button,
  Divider,
  Backdrop,
  TextField,
  Typography,
  Grid2 as Grid,
  CircularProgress,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import apiService from 'library/api-service';
import { useCallback, useState } from 'react';
import Container from '@mui/material/Container';
import { Link, useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import logoSrc from 'assets/images/idecon-header-logo.png';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { Copyright, LanguagePicker, ColorModeIconDropdown } from 'components';

export type SignUpFormParams = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

export type SignUpPostParams = {
  nameSurname: string;
  email: string;
  password: string;
};

const defaultValues: SignUpFormParams = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
};

const SignUp = () => {
  // other hooks
  const intl = useIntl();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);

  // form validations & form state
  const schema = yup.object().shape({
    firstName: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    lastName: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    email: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .email(intl.formatMessage({ id: 'login.invalidEmail', defaultMessage: 'Geçersiz e-posta formatı' }))
      .matches(
        /^[a-zA-Z0-9._%+-]+@(idecon\.com\.tr|ehesapp\.com)$/,
        intl.formatMessage({
          id: 'validations.invalidEmailForIdeconAndEhesapp',
          defaultMessage: 'Idecon ya da Ehesapp e-postası girilmelidir',
        })
      ),
    password: yup
      .string()
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
  });

  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpFormParams>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // async helper functions
  const onSubmit = async (data: SignUpFormParams) => {
    const postData: SignUpPostParams = {
      nameSurname: `${data.firstName} ${data.lastName}`,
      email: data.email,
      password: data.password,
    };

    try {
      setLoading(true);

      const response = await apiService.signIn(postData);

      if (response.isSuccess) {
        enqueueSnackbar(response.message, { variant: 'success' });
        reset();
        onClickLogoHandler();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  // sync helper functions
  const onClickLogoHandler = useCallback(() => navigate('login'), [navigate]);

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900]),
      }}>
      <Container sx={{ pt: 3 }} component="main" maxWidth="sm">
        <Paper elevation={2}>
          <Paper elevation={0}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 20px',
              }}>
              <img style={{ cursor: 'pointer' }} onClick={onClickLogoHandler} width="100px" src={logoSrc} alt="logo" />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 2,
                }}>
                <LanguagePicker />
                <ColorModeIconDropdown />
              </Box>
            </Box>
          </Paper>
          <Divider />
          <Box
            sx={{
              padding: '20px 30px',
              marginTop: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
            <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
              <LockOutlinedIcon />
            </Avatar>
            <Typography sx={{ fontWeight: 'bold' }} component="h1" variant="h5">
              <FormattedMessage id="signUp.signUp" defaultMessage="Kaydol" />
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        label={<FormattedMessage id="signUp.name" defaultMessage="Ad" />}
                        fullWidth
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        autoFocus
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        label={<FormattedMessage id="signUp.surname" defaultMessage="Soyad" />}
                        fullWidth
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        label={<FormattedMessage id="signUp.email" defaultMessage="E-Posta" />}
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid size={12}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field: { ref, ...rest } }) => (
                      <TextField
                        {...rest}
                        label={<FormattedMessage id="signUp.password" defaultMessage="Parola" />}
                        type="password"
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        autoComplete="new-password"
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
                <FormattedMessage id="signUp.signUp" defaultMessage="Kaydol" />
              </Button>
              <Grid container justifyContent="flex-end">
                <Grid>
                  <Link to="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <FormattedMessage
                      id="signUp.alreadyHaveAnAccount"
                      defaultMessage="Zaten hesabınız var mı? Giriş Yapın"
                    />
                  </Link>
                </Grid>
              </Grid>
            </Box>
            <Copyright />
          </Box>
        </Paper>
      </Container>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default SignUp;
